import { isIntegrationError } from '@accedo/one-plugin-sdk';

import { Errors, errorToCode, fatalErrors } from './errors';

export interface OneErrorDetails {
  requestIds?: string[];
  cdnRequestIds?: string[];
  relatedErrorCodes?: string[];
  extras?: Record<string, unknown>;
  // the original caught error (if any), just like in the native `Error.cause`
  cause?: unknown;
  // prevent this error from being sent to Sentry
  preventSentry?: boolean;
}

export class OneError extends Error {
  details?: OneErrorDetails;
  code: string;

  constructor(message: Errors, details?: OneErrorDetails) {
    super(message, { cause: details?.cause });
    this.name = 'OneError';
    this.details = details;
    this.code = errorToCode[message];
  }
}

/**
 * Type guard to check if an error is a OneError.
 * @param error Error to verify.
 * @returns True if the error is a OneError, false otherwise.
 */
export const isOneError = (error?: Error): error is OneError => {
  return error instanceof OneError;
};

/**
 * Checks if an error is fatal and should be retryable.
 * @param error Error to verify.
 * @returns True if the error is fatal, false otherwise.
 */
export const isFatalError = (error: OneError | Error) =>
  fatalErrors.includes(error.message);

/** Does this error message match one of the defined error keys in the Errors enum? */
const isOneErrorKey = (message: string): message is Errors =>
  Object.values(Errors).includes(message);

/**
 * Map error into OneError.
 * @param error Error that we want to verify and map to OneError.
 * @returns The mapped OneError or the original error if it's not mappable.
 */
export const mapToOneError = (error: unknown) => {
  // errors from try-catch are actually `unknown` in strict mode.
  // convert those to errors, so the rest of the app can continue to assume `Error`.
  if (!(error instanceof Error)) {
    return new Error(String(error));
  }

  // does the error message will map to a OneError key?
  const { message } = error;

  if (!isOneErrorKey(message)) {
    return error;
  }

  return new OneError(
    message,
    isIntegrationError(error) ? error.details : undefined
  );
};

/**
 * Resolves the translation message keys (most specific first) and interpolation context for
 * displaying a caught error. A mapped `OneError` yields its code-derived key
 * (`errors.<code>.message`) plus any `details.extras` for interpolation, always falling back
 * to the provided generic key. Pass the result straight to `tb(keys, context)`.
 * @param error The caught error to resolve a message for.
 * @param fallbackKey Generic translation key used when the error is unmapped, or as a fallback.
 */
export const getErrorMessageKeys = (
  error: unknown,
  fallbackKey: string
): { keys: string[]; context?: OneErrorDetails['extras'] } => {
  const oneError = mapToOneError(error);

  if (!isOneError(oneError)) {
    return { keys: [fallbackKey] };
  }

  return {
    keys: [`errors.${oneError.code.toLowerCase()}.message`, fallbackKey],
    context: oneError.details?.extras,
  };
};
